<template>
    <v-text-field
        ref="textField"
        :value="value"
        :class="{'required': isRequired, 'centered-input': centerText}"
        :append-icon="appendIcon"
        :autofocus="autofocus"
        :browser-autocomplete="allowAutocomplete ? undefined : 'nope'"
        :clearable="isClearable"
        :color="color"
        :counter="counter"
        :dark="dark"
        :disabled="disabled"
        :error="error"
        :error-messages="errorMessages"
        :flat="flat"
        :height="height"
        :hide-details="hideDetails"
        :hint="hint"
        :id="id"
        :label="label"
        :loading="loading"
        :mask="mask"
        :maxLength="maxLength"
        :persistent-hint="persistentHint"
        :placeholder="placeholder"
        :prepend-icon="icon ? icon : ''"
        :prefix="prefix"
        :readonly="readonly"
        :rules="rules"
        :single-line="singleLine"
        :solo="solo"
        :success="success"
        :success-messages="successMessages"
        :suffix="suffix"
        :type="type"
        :validate-on-blur="validateOnBlur"
        @blur="handleBlur"
        @click:append="handleAppendClick"
        @click:prepend="handlePrependClick"
        @focus="handleFocus"
        @input="handleInput"
        @keyup.enter="handleEnter"
    ></v-text-field>
</template>

<style lang="stylus">
    .centered-input input
        text-align center

    :-webkit-autofill
        animation-name onAutoFillStart

    :not(:-webkit-autofill)
        animation-name onAutoFillCancel

    @keyframes onAutoFillStart
        from
            color inherit
        to
            color inherit

    @keyframes onAutoFillCancel
        from
            color inherit
        to
            color inherit
</style>

<script>
    import WFieldChecksMixin from '../../mixins/forms/w-field-checks-mixin'

    /**
     * Text field for form input. Highly configurable and supports validation.
     */
    export default {
        name: 'WTextField',
        mixins: [WFieldChecksMixin],
        data: () => ({autofilled: false}),
        props: {
            /**
             * Append the field with an icon. Set to false if no icon desired.
             */
            appendIcon: {
                type: String,
                default: ''
            },
            /**
             * Focus the field (so user can enter text straight away).
             */
            autofocus: {
                type: Boolean,
                default: false
            },
            /**
             * Center the text inside the text field.
             */
            centerText: {
                type: Boolean,
                default: false
            },
            /**
             * Colour of the field.
             */
            color: {
                type: String,
                default: 'primary'
            },
            /**
             * Count the characters/number of selections.
             */
            counter: {
                type: [Boolean, Number],
                default: false
            },
            /**
             * Apply Vuetify's dark styling (inverts the colours in most cases).
             */
            dark: {
                type: Boolean,
                default: false
            },
            /**
             * Disable the field.
             */
            disabled: {
                type: Boolean,
                default: false
            },
            /**
             * Do not elevate the text field.
             */
            flat: {
                type: Boolean,
                default: false
            },
            /**
             * Height of the text field.
             */
            height: {
                type: [Number, String],
                default: undefined
            },
            /**
             * Hide extra details such as the hint. Helps keep forms lean.
             */
            hideDetails: {
                type: Boolean,
                default: false
            },
            /**
             * Display a hint under the field when entering text.
             */
            hint: {
                type: String,
                default: ''
            },
            /**
             * Prepend the field with an icon. Set to false if no icon desired.
             */
            icon: {
                type: [Boolean, String],
                required: true,
                validator: (val) => typeof val === 'string' || val === false
            },
            /**
             * Label to display in the field.
             */
            label: {
                type: String,
                required: true
            },
            /**
             * Show a loading bar at the bottom of the field entry box.
             */
            loading: {
                type: Boolean,
                default: false
            },
            /**
             * Apply a character mask to the text field.
             */
            mask: {
                type: String,
                default: ''
            },
            /**
             * Apply a hard character limit to the field.
             */
            maxLength: {
                type: [Number, Boolean],
                default: false
            },
            /**
             * Make the hint persistent (shows even when the field is not focused).
             */
            persistentHint: {
                type: Boolean,
                default: false
            },
            /**
             * Placeholder text to display in the field.
             */
            placeholder: {
                type: String,
                default: ''
            },
            /**
             * Prefix for the field.
             */
            prefix: {
                type: String,
                default: ''
            },
            /**
             * Set the field to read only.
             */
            readonly: {
                type: Boolean,
                default: false
            },
            /**
             * Set the field to be a single line (label doesn't move to the top on focus etc.).
             */
            singleLine: {
                type: Boolean,
                default: false
            },
            /**
             * Apply solo styling to the input (box-style).
             */
            solo: {
                type: Boolean,
                default: false
            },
            /**
             * Put the field in a success state.
             */
            success: {
                type: Boolean,
                default: false
            },
            /**
             * Display success message(s) and style the field green.
             */
            successMessages: {
                type: [String, Array],
                default: () => []
            },
            /**
             * Add a suffix to the field e.g. 'tickets'.
             */
            suffix: {
                type: String,
                default: ''
            },
            /**
             * The input type of the field e.g. 'number', 'datetime', 'password', etc.
             */
            type: {
                type: String,
                default: 'text'
            },
            /**
             * The value of the field. Accepts being used for an initial value (e.g. for update forms).
             */
            value: {
                type: [String, Number],
                value: ''
            }
        },
        methods: {
            getInputElement () {
                const ref = 'textField'
                let el = null

                if (this.$refs[ref] && (el = this.$refs[ref].$el)) {
                    return el.querySelector('input')
                }

                return el
            },
            handlePrependClick () {
                this.$refs.textField.focus()

                /**
                 * Prepend click event.
                 * @type {undefined}
                 */
                this.$emit('click:prepend')
            },
            checkAutofill (e) {
                if (e.animationName === 'onAutoFillStart') {
                    this.autofilled = true
                } else if (e.animationName === 'onAutoFillCancel') {
                    this.autofilled = false
                }
            }
        },
        watch: {
            autofilled (newVal, oldVal) {
                if (newVal !== oldVal) {
                    /**
                     * autofill event.  Used to check if field is autofilled
                     * @type {boolean}
                     */
                    this.$emit('autofill', this.autofilled)
                }
            }
        },
        mounted () {
            const vm = this
            let inputEl
            let intervalId = setInterval(() => {
                inputEl = vm.getInputElement()
                if (inputEl) {
                    inputEl.addEventListener('keypress', vm.checkAccounting)
                    inputEl.addEventListener('animationstart', vm.checkAutofill)
                    clearInterval(intervalId)
                }
            }, 100)
        }
    }
</script>
